import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../api.js';
import { goBack, navigate, showToast, friendlyError } from '../store.js';
import { __ } from '../i18n.js';
import { formatMinutes, deadlineChipLabel } from '../lib/format.js';
import { PROJECT_IMPORTANCE_OPTIONS, projectStatusInfo } from '../lib/projects.js';
import { IMPACT_OPTIONS, PRIORITY_LEVELS } from '../lib/labels.js';
import Header from '../components/Header.jsx';
import BackButton from '../components/BackButton.jsx';
import Icon from '../components/Icon.jsx';

function optionLabel(options, value) {
  const found = options.find((o) => o.value === value);
  return found ? found.label() : '';
}

/**
 * HH-8: project detail — its own metadata, its tasks (existing GET /tasks +
 * a project_id filter, per the brief; no nested /projects/{id}/tasks
 * route), the "maak dit de eerstvolgende stap" promotion action, and the
 * soft "project klaar?" prompt. Fetches fresh via GET /projects/{id} on
 * every mount (the same "screens remount on nav, so that's the refresh"
 * convention ManageScreen/SubscriptionsScreen/ListsScreen already rely on —
 * see app.jsx's per-frame key) rather than trusting a list row passed
 * through nav params, so open_task_count/completed_task_count are always
 * current.
 */
export default function ProjectScreen({ params }) {
  const projectId = params?.projectId;

  const [project, setProject] = useState(null);
  const [taskTab, setTaskTab] = useState('active');
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [markingDone, setMarkingDone] = useState(false);

  // Review finding: reloadProject() is called both from the mount effect
  // below and, later, from handleMarkDone() — without a mount guard, a
  // response landing after the user has already navigated away (this
  // screen unmounted, per app.jsx's per-frame key) could still fire
  // goBack() against whatever frame is now actually on top of the shared
  // nav stack. A ref (not a per-effect `cancelled` flag, since this needs
  // to guard a function called from more than one place) tracks whether
  // this instance is still the one on screen.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  function reloadProject() {
    return api.projects
      .get(projectId)
      .then((res) => {
        if (mountedRef.current) setProject(res.project);
      })
      .catch((err) => {
        if (mountedRef.current) {
          showToast(friendlyError(err), 'error');
          goBack();
        }
      });
  }

  useEffect(() => {
    reloadProject();
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    setTasksLoading(true);
    api.tasks
      .list({ status: taskTab, project_id: projectId })
      .then((res) => {
        if (!cancelled) setTasks(res.tasks || []);
      })
      .catch((err) => {
        if (!cancelled) showToast(friendlyError(err), 'error');
      })
      .finally(() => {
        if (!cancelled) setTasksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, taskTab]);

  if (!project) {
    return (
      <>
        <BackButton onClick={goBack} label={__('Taken', 'etm')} />
        <Header title={__('Project', 'etm')} />
      </>
    );
  }

  const status = projectStatusInfo(project.status);

  // "geen eerstvolgende stap" (brief): nothing open AND nothing ever
  // completed here — a brand new project with no next step yet. Excludes a
  // project already marked 'done' (review finding): promoting a next step
  // on a project the user explicitly closed out contradicts that decision,
  // so the whole promotion block below is gated on status !== 'done'.
  const showNoNextStepHint = project.open_task_count === 0 && project.completed_task_count === 0 && project.status !== 'done';
  // The soft "project klaar?" prompt (brief: "never an auto-status-change"):
  // this project DID have work, all of it is now done, and it isn't already
  // marked done — distinct from showNoNextStepHint, which is a project that
  // never had a task at all.
  const showDonePrompt = project.open_task_count === 0 && project.completed_task_count > 0 && project.status !== 'done';

  async function handlePromote() {
    // HH-8 promotion flow: "maak dit de eerstvolgende stap" opens the
    // ordinary task form prefilled with this project's id — a create-mode
    // prefill, no id, same shape WP-6's "Meer details" handoff uses.
    navigate('taskForm', { task: { project_id: project.id }, promotionProject: project });
  }

  async function handleMarkDone() {
    setMarkingDone(true);
    try {
      await api.projects.update(project.id, { status: 'done' });
      showToast(__('Project gemarkeerd als klaar.', 'etm'), 'success');
      await reloadProject();
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setMarkingDone(false);
    }
  }

  return (
    <div>
      <BackButton onClick={goBack} label={__('Taken', 'etm')} />
      <Header
        title={project.title}
        actions={
          <button
            type="button"
            class="etm-icon-btn"
            aria-label={__('Project bewerken', 'etm')}
            onClick={() => navigate('projectForm', { project })}
          >
            <Icon name="edit" size={20} />
          </button>
        }
      />

      <div class="etm-project-meta">
        <span class="etm-chip">
          <Icon name={status.icon} size={14} /> {status.label()}
        </span>
        {project.target_date ? (
          <span class="etm-chip">
            <Icon name="flag" size={14} /> {deadlineChipLabel(project.target_date)}
          </span>
        ) : null}
        {project.is_shared ? <span class="etm-field-hint">{__('gedeeld', 'etm')}</span> : null}
      </div>

      {project.description ? <p class="etm-project-description">{project.description}</p> : null}

      <div class="etm-project-stats">
        <span class="etm-field-hint">{optionLabel(PROJECT_IMPORTANCE_OPTIONS, project.importance)}</span>
        <span class="etm-field-hint">{optionLabel(IMPACT_OPTIONS, project.impact)}</span>
        <span class="etm-field-hint">{optionLabel(PRIORITY_LEVELS, project.priority)}</span>
      </div>

      {showDonePrompt ? (
        <div class="etm-project-prompt">
          <Icon name="celebration" size={20} />
          <span>{__('Alle taken in dit project zijn afgerond. Project klaar?', 'etm')}</span>
          <button type="button" class="etm-btn etm-btn-primary" disabled={markingDone} onClick={handleMarkDone}>
            {__('Markeer als klaar', 'etm')}
          </button>
        </div>
      ) : null}

      {project.status !== 'done' ? (
        <>
          <button type="button" class="etm-btn etm-btn-primary" onClick={handlePromote}>
            <Icon name="add" size={20} /> {__('Maak dit de eerstvolgende stap', 'etm')}
          </button>
          {showNoNextStepHint ? (
            <p class="etm-field-hint">{__('Geen eerstvolgende stap. Geef dit project een concrete eerste taak.', 'etm')}</p>
          ) : null}
        </>
      ) : null}

      <div class="etm-tabs">
        <button
          type="button"
          class={`etm-tab-btn${taskTab === 'active' ? ' active' : ''}`}
          onClick={() => setTaskTab('active')}
        >
          {__('Actief', 'etm')}
        </button>
        <button
          type="button"
          class={`etm-tab-btn${taskTab === 'completed' ? ' active' : ''}`}
          onClick={() => setTaskTab('completed')}
        >
          {__('Voltooid', 'etm')}
        </button>
      </div>

      {tasks.length === 0 && !tasksLoading ? (
        <div class="etm-empty-state">
          {taskTab === 'active' ? __('Geen actieve taken in dit project.', 'etm') : __('Nog geen voltooide taken.', 'etm')}
        </div>
      ) : (
        <div class="etm-task-list">
          {tasks.map((task) => {
            const deadlineLabel = task.deadline ? deadlineChipLabel(task.deadline) : null;
            return (
              <div
                key={task.id}
                class="etm-task-list-item"
                role="button"
                tabIndex={0}
                onClick={() => navigate('taskForm', { task })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('taskForm', { task });
                  }
                }}
              >
                <div class="etm-task-list-item__body">
                  <div class="etm-task-list-item__title">{task.title}</div>
                  <div class="etm-task-list-item__meta">
                    <span>{formatMinutes(task.effort_minutes)}</span>
                    {deadlineLabel ? (
                      <span class="etm-chip">
                        <Icon name="calendar_month" size={14} /> {deadlineLabel}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
